import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/MockAuthContext';
import LoginModal from './Auth/LoginModal';

const NavContainer = styled.nav<{ isVisible: boolean }>`
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  color: white;
  padding: 0;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  transform: translateY(${props => props.isVisible ? '0' : '-100%'});
  transition: transform 0.3s ease-in-out;
`;

const NavInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 1rem;
  }
`;

const Logo = styled(Link)`
  font-size: 1.8rem;
  font-weight: bold;
  text-decoration: none;
  color: white;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: #74b9ff;
  }

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
`;

const NavLinks = styled.div<{ isOpen: boolean }>`
  display: flex;
  gap: 2rem;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    flex-direction: column;
    gap: 1rem;
    width: 100%;
  }
`;

const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: 500;
  padding: 1rem 0;
  transition: color 0.3s ease;
  
  &:hover {
    color: #74b9ff;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0;
    border-bottom: 1px solid rgba(255,255,255,0.1);
  }
`;

const MobileToggle = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  position: absolute;
  right: 1rem;
  top: 1.2rem;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const AuthSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    width: 100%;
    margin-top: 1rem;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const AuthButton = styled.button`
  background: linear-gradient(135deg, #74b9ff 0%, #0984e3 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(116, 185, 255, 0.3);
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const UserName = styled.span`
  font-weight: 500;
`;

const UserRole = styled.span<{ role: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: bold;
  background: ${props => props.role === 'admin' ? '#f39c12' : '#74b9ff'};
  color: white;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: bold;
  background: ${props => {
    switch (props.status) {
      case 'approved': return '#00b894';
      case 'pending': return '#fdcb6e';
      case 'rejected': return '#e17055';
      default: return '#ddd';
    }
  }};
  color: ${props => props.status === 'pending' ? '#2c3e50' : 'white'};
`;

const Navigation = React.memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { currentUser, userProfile, logout, isAdmin } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show header when at top (within 10px)
      if (currentScrollY < 10) {
        setIsVisible(true);
      }
      // Hide header when scrolling down
      else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      }
      // Show header when scrolling up
      else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleLogout = async () => {
    try {
      await logout();
      setIsOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      <NavContainer isVisible={isVisible}>
        <NavInner>
          <Logo to="/">
            <LogoImage src="/Waterfall logo.png" alt="Waterfall Explorer Logo" />
            Waterfall Explorer
          </Logo>
          <MobileToggle onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? '✕' : '☰'}
          </MobileToggle>
          <NavLinks isOpen={isOpen}>
            <NavLink to="/" onClick={() => setIsOpen(false)}>Home</NavLink>
            <NavLink to="/states" onClick={() => setIsOpen(false)}>Waterfalls by State</NavLink>
            <NavLink to="/about" onClick={() => setIsOpen(false)}>About Us</NavLink>
            {isAdmin() && (
              <NavLink to="/admin" onClick={() => setIsOpen(false)}>Admin Dashboard</NavLink>
            )}
          </NavLinks>
          
          <AuthSection>
            {currentUser ? (
              <>
                <UserInfo>
                  <UserName>{userProfile?.displayName}</UserName>
                  <UserRole role={userProfile?.role || 'user'}>
                    {userProfile?.role}
                    {userProfile?.isFirstUser && ' 👑'}
                  </UserRole>
                  <StatusBadge status={userProfile?.status || 'pending'}>
                    {userProfile?.status}
                  </StatusBadge>
                </UserInfo>
                <AuthButton onClick={handleLogout}>Logout</AuthButton>
              </>
            ) : (
              <AuthButton onClick={() => setShowLoginModal(true)}>Login</AuthButton>
            )}
          </AuthSection>
        </NavInner>
      </NavContainer>
      
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
      />
    </>
  );
});

export default Navigation;